/**
 * Observation schemas — assessment scores, supervision compliance/note quality.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type {
  AssessmentLoincCode as FhirLoincCode,
  AssessmentSeverity as FhirSeverity,
  ComplianceCode as FhirComplianceCode,
  NoteQualityCode as FhirNoteQualityCode,
} from '@unify/fhir-model/resources/observation';

/** LOINC codes for assessment instruments — synced with fhir-model. */
export const assessmentLoincValues = [
  '44249-1',
  '70274-6',
] as const satisfies readonly FhirLoincCode[];

/** Assessment severity interpretations — synced with fhir-model. */
export const assessmentSeverityValues = [
  'Minimal',
  'Mild',
  'Moderate',
  'Severe',
] as const satisfies readonly FhirSeverity[];

/**
 * Assessment score Observation — boundary validation schema for mapper output.
 */
export const assessmentObservationSchema = z.object({
  id: z.string(),
  loincCode: z.string(),
  instrumentName: z.string().optional(),
  score: z.number(),
  maxScore: z.number().optional(),
  severity: z.enum(assessmentSeverityValues).optional(),
  patientId: z.string(),
  questionnaireResponseId: z.string().optional(),
  effectiveDateTime: z.string().optional(),
});

export type AssessmentObservationMapped = z.infer<typeof assessmentObservationSchema>;

/** Supervision compliance codes — synced with fhir-model. */
export const complianceCodeValues = [
  'weekly-hour-total',
  'daily-hour-total',
  'monthly-hour-total',
  'admin-hour-overage',
  'months-over-130',
] as const satisfies readonly FhirComplianceCode[];

/** Note quality codes — synced with fhir-model. */
export const noteQualityCodeValues = [
  'edit-distance',
  'adjustment-disorder',
] as const satisfies readonly FhirNoteQualityCode[];
